import { useState } from "react";
import type { FormEvent } from "react";
import PageHero from "@/components/feature/PageHero";

const FORM_SUBMIT_URL = "https://readdy.ai/api/form/db8hfdbk13ra8mig8rqg";

const services = [
  {
    icon: "ri-live-line",
    title: "赛事直播入口",
    desc: "聚合欧冠赛事的直播观看入口，实时比分与赛事实况同步展示，方便快速进入观赛页面。",
  },
  {
    icon: "ri-calendar-2-line",
    title: "赛程与结果查询",
    desc: "按赛季和日期浏览完整赛程，查看对阵、开赛时间、比赛状态与最终比分。",
  },
  {
    icon: "ri-shield-star-line",
    title: "球队资料库",
    desc: "收录参赛球队的历史、阵容名单、主教练信息与赛季统计数据，方便深度了解球队。",
  },
  {
    icon: "ri-newspaper-line",
    title: "欧冠资讯聚合",
    desc: "整合赛事战报、球队动态、转会消息与数据分析，覆盖赛场内外的最新资讯。",
  },
];

const contactInfo = [
  { icon: "ri-mail-line", label: "合作与反馈邮箱", value: "hello@ucl-live.example" },
  { icon: "ri-phone-line", label: "内容纠错热线", value: "400-000-2026（工作日 10:00-18:00）" },
  { icon: "ri-map-pin-line", label: "办公地点", value: "上海市浦东新区体育大厦 12 层" },
];

export default function About() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      return;
    }

    const name = String(formData.get("name") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();

    if (!name || !email || !message) {
      setStatus("error");
      setFormError("请填写姓名、邮箱与留言内容后再提交。");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setFormError("邮箱格式不正确，请检查后重试。");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";

      if (!response.ok || parsed?.code !== "OK" || /spam/i.test(serverMsg)) {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试，或通过邮箱与我们联系。");
        return;
      }

      setStatus("success");
      setFormError("");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="w-full">
      <PageHero
        eyebrow="ABOUT US"
        title="关于欧冠直播"
        description="我们是一个专注欧洲冠军联赛的赛事资讯与直播入口平台，为球迷提供赛程、球队资料与最新动态。"
        crumbs={[{ label: "首页", to: "/" }, { label: "关于我们" }]}
      />

      <section className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <h2 className="font-heading text-2xl font-semibold text-foreground-950 mb-4">
              网站介绍
            </h2>
            <div className="flex flex-col gap-4">
              <p className="text-sm md:text-base text-foreground-700 leading-loose">
                欧冠直播（UCL LIVE）是一个面向足球爱好者的欧冠主题资讯平台。我们把赛事直播入口、完整赛程、球队资料与赛事新闻整理在一起，帮助你在最短时间内找到想看的信息。
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-loose">
                网站定位为“欧洲冠军联赛足球比赛的实时直播和资讯平台”，目标用户包括足球迷、体育媒体从业者以及关注欧冠赛事的观众。我们希望无论是赛前查赛程、赛中看比分，还是赛后读战报，都能在这里获得顺畅的体验。
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-loose">
                需要说明的是，当前站内的赛事、球队与资讯内容均为虚构演示数据，用于展示各模块的展示效果；直播播放器为前端演示区域，真实直播画面需要接入第三方直播源。
              </p>
            </div>

            <h2 className="font-heading text-2xl font-semibold text-foreground-950 mt-10 mb-4">
              服务范围
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {services.map((s) => (
                <div
                  key={s.title}
                  className="rounded-lg border border-background-200 bg-background-100 p-5"
                >
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-accent-500 text-background-50 text-xl">
                    <i className={s.icon} aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-3 text-base font-semibold text-foreground-950">{s.title}</h3>
                  <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-1">
                联系方式与反馈
              </h2>
              <p className="text-sm text-foreground-600 mb-5">
                有内容纠错、合作意向或使用建议？填写下方表单告诉我们，我们会尽快回复。
              </p>

              {status === "success" ? (
                <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-6 text-center animate-fade-in">
                  <span className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-secondary-500 text-background-50 text-2xl">
                    <i className="ri-check-line" aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                    感谢你的反馈！
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600">
                    我们已收到你的留言，会尽快通过邮箱与你联系。
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="mt-5 inline-flex items-center gap-2 rounded-md border border-secondary-300 px-4 py-2 text-sm font-semibold text-secondary-800 hover:bg-secondary-100 transition-colors whitespace-nowrap"
                  >
                    再次留言
                  </button>
                </div>
              ) : (
                <form
                  id="ucl-feedback-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-4"
                >
                  <div>
                    <label
                      htmlFor="ucl-name"
                      className="block text-sm font-medium text-foreground-800 mb-1.5"
                    >
                      姓名 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="ucl-name"
                      type="text"
                      name="name"
                      required
                      placeholder="请输入你的姓名"
                      className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="ucl-email"
                      className="block text-sm font-medium text-foreground-800 mb-1.5"
                    >
                      邮箱 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="ucl-email"
                      type="email"
                      name="email"
                      required
                      placeholder="用于接收我们的回复"
                      className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="ucl-type"
                      className="block text-sm font-medium text-foreground-800 mb-1.5"
                    >
                      反馈类型
                    </label>
                    <select
                      id="ucl-type"
                      name="feedback_type"
                      defaultValue="内容纠错"
                      className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200"
                    >
                      <option value="内容纠错">内容纠错</option>
                      <option value="功能建议">功能建议</option>
                      <option value="商务合作">商务合作</option>
                      <option value="其他问题">其他问题</option>
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="ucl-message"
                      className="block text-sm font-medium text-foreground-800 mb-1.5"
                    >
                      留言内容 <span className="text-accent-600">*</span>
                    </label>
                    <textarea
                      id="ucl-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="请描述你的问题或建议（最多 500 字）"
                      className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 resize-none"
                    ></textarea>
                    <p className="mt-1 text-xs text-foreground-500">最多 500 个字符</p>
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    className="ucl-form-extra"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {status === "error" && formError && (
                    <div className="rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-accent-900 animate-fade-in">
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-accent-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                  >
                    {status === "submitting" ? (
                      <>
                        <span className="w-4 h-4 rounded-full border-2 border-background-200 border-t-background-50 animate-spin"></span>
                        提交中…
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line" aria-hidden="true"></i>
                        提交留言
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5">
              <h3 className="font-heading text-base font-semibold text-foreground-950 mb-3">
                其他联系方式
              </h3>
              <ul className="flex flex-col gap-3">
                {contactInfo.map((c) => (
                  <li key={c.label} className="flex items-start gap-3">
                    <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-primary-500 text-background-50">
                      <i className={c.icon} aria-hidden="true"></i>
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs text-foreground-500">{c.label}</p>
                      <p className="text-sm font-medium text-foreground-900 break-words">
                        {c.value}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}